import React, { useState, useRef, useEffect, useMemo } from "react";
import { v4 } from "uuid";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { Product, ProductType } from "../../models/Product";
import { RoleType } from "../../models/Role";
import { NewProductForm } from "../../forms/NewProductForm/NewProductForm";
import { activeUser } from "../../services/AuthService";
import {
  getAllProducts,
  deleteOneProduct,
} from "../../services/ProductService";
import "./ProductsPage.scss";

export const ProductsPage = () => {
  const { t } = useTranslation();
  const toast = useRef(null);

  const [processing, setProcessing] = useState(false);

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const [showForm, setShowForm] = useState<string>("");

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [productToDelete, setProductToDelete] = useState<Product | undefined>();

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    categoryName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    measuringUnit: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const canDelete =
    activeUser?.role?.name === RoleType.Superadmin ||
    activeUser?.role?.name === RoleType.Administrator;

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshProducts = async () => {
    setProcessing(true);
    try {
      const resp = await getAllProducts();
      ensureSuccess(resp);

      const list: Product[] = resp?.data?.result ?? resp?.data ?? [];

      const normalized: any[] = list.map((p: any) => ({
        ...p,
        categoryName: p?.category?.name ?? "",
      }));

      setAllProducts(normalized as Product[]);
    } catch (e: any) {
      //@ts-ignore
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ?? String(t("Greška pri učitavanju robe")),
        life: 3500,
      });
      setAllProducts([]);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    refreshProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableValue = useMemo(() => allProducts, [allProducts]);

  const typeOptions = useMemo(
    () => [
      { label: t("Svi tipovi"), value: null },
      { label: t("Repromaterijal"), value: ProductType.RawMaterial }, // "RAW_MATERIAL"
      { label: t("Merkantila"), value: ProductType.MercantileGoods }, // "MERCANTILE_GOODS"
    ],
    [t]
  );

  const typeFilterElement = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={typeOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={String(t("Izaberite"))}
        className="p-column-filter"
        showClear={false}
      />
    );
  };

  const typeBody = (row: any) => {
    return row?.type === ProductType.MercantileGoods
      ? t("Merkantila")
      : t("Repromaterijal");
  };

  const categoryBody = (row: any) => <span>{row?.category?.name ?? ""}</span>;

  const actionsBody = (row: Product) => {
    return (
      <div className="action-product-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            setSelectedProduct(cloneDeep(row));
            setShowForm(v4());
          }}
        />

        {canDelete && (
          <ButtonInput
            addStyle={{
              border: "none",
              background: "#198754",
              marginLeft: "1rem",
            }}
            labelText={""}
            icon="bi bi-trash"
            onButtonClick={() => {
              setProductToDelete(row);
              setDeleteConfirmVisible(true);
            }}
          />
        )}
      </div>
    );
  };

  const acceptDelete = async () => {
    if (!productToDelete?.id) return;

    try {
      const resp = await deleteOneProduct(productToDelete.id);
      ensureSuccess(resp);

      //@ts-ignore
      toast.current?.show({
        severity: "success",
        detail: String(t("Proizvod je izbrisan")),
        life: 3000,
      });

      setDeleteConfirmVisible(false);
      setProductToDelete(undefined);

      await refreshProducts();
    } catch (e: any) {
      //@ts-ignore
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ?? String(t("Greška pri brisanju robe")),
        life: 3500,
      });
      setDeleteConfirmVisible(false);
    }
  };

  return (
    <PageLayout>
      <Toast ref={toast} position="top-right" />
      <div className="table-wrapper">
        <div className="table-header">
          <div style={{ display: "flex", alignItems: "center" }}>
            <i
              className="bi bi-box-seam"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
                marginRight: "1rem",
              }}
            />
            <span className="header-left-title">
              {t("Lista registrovane robe u sistemu")}
            </span>
          </div>

          <div>
            <NewProductForm
              buttonLabel={String(t("Dodaj robu"))}
              buttonIcon="bi bi-trash2"
              show={showForm}
              product={selectedProduct}
              buttonStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
              }}
              buttonSaveStyle={{
                border: "none",
                background: "rgba(0, 165, 83, 1)",
              }}
              buttonResetStyle={{
                border: "none",
                background: "rgba(220, 53, 69, 1)",
              }}
              refreshingData={refreshProducts}
            />
          </div>
        </div>

        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              value={tableValue}
              dataKey="id"
              scrollable
              scrollHeight="80vh"
              stripedRows
              showGridlines
              removableSort
              sortMode="single"
              filterDisplay="row"
              filters={filters}
              onFilter={(e) => {
                setFilters(e.filters as DataTableFilterMeta);
                setFirst(0);
              }}
              paginator
              first={first}
              rows={rows}
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate=""
              emptyMessage={t("Nema robe za zadate kriterijume")}
            >
              <Column
                field="code"
                header={t("Šifra robe")}
                sortable
                filter
                style={{ minWidth: "160px" }}
              />
              <Column
                field="name"
                header={t("Naziv robe")}
                sortable
                filter
                style={{ minWidth: "260px" }}
              />
              <Column
                field="type"
                header={t("Tip robe")}
                body={typeBody}
                sortable
                filter
                filterElement={typeFilterElement}
                showFilterMenu={false}
                style={{ minWidth: "170px" }}
              />
              <Column
                field="categoryName"
                header={t("Vrsta robe")}
                body={categoryBody}
                sortable
                filter
                style={{ minWidth: "220px" }}
              />
              <Column
                field="measuringUnit"
                header={t("Merna jedinica")}
                sortable
                filter
                style={{ minWidth: "170px" }}
              />
              <Column
                header={t("Opcije")}
                body={actionsBody}
                style={{ minWidth: "120px", textAlign: "center" }}
              />
            </DataTable>
          </div>
        )}
      </div>

      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t("Da li ste sigurni da želite da obrišete proizvod?")}
        header={t("Potvrda brisanja proizvoda")}
        icon="bi bi-exclamation-circle"
        acceptLabel={String(t("Da"))}
        acceptClassName="accept-button"
        accept={acceptDelete}
        rejectLabel={String(t("Ne"))}
        rejectClassName="reject-button"
        reject={() => {
          setDeleteConfirmVisible(false);
          setProductToDelete(undefined);
        }}
      />
    </PageLayout>
  );
};
